import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { cityBridge } from './cityBridge';
import { buildRoadMap, ROAD, WALK } from './roadMap';
import { streetU } from './streetMaterials';
import { sky } from './skyState';
import { QUALITY } from './quality';
import { roadMapBridge } from './roadMapBridge';
import { weather } from './weather';

// ── THE CITY AT NIGHT ────────────────────────────────────────────────────────
//
// The model ships with ~30 lamp heads, all on one strip; every other street was
// unlit, so after dark the city was a black floor under glowing towers. This
// lights all of it:
//
//   · reads the street layout off the model (roadMap) and stands a lamp post
//     on the kerb every ~13 units, arm reaching out over the road;
//   · paints every lamp's pool of light — and the model's own lamps' — into one
//     texture the city's materials sample (streetMaterials): light on the
//     asphalt, the pavement and the lower walls, at the cost of one texture read;
//   · a faint cone of light under each head, which with the haze and the bloom
//     is the "light in the air" of a city at night;
//   · all of it rises with dusk and falls with dawn, from the shared sky.
//
// Posts are two instanced draws, cones one, so the whole system is three draw
// calls whatever the lamp count. Posts are real geometry and cast real shadows
// in the low sun (WorldLight's sweep picks them up).

const SPACING = 13;          // world units between lamps along a kerb
const KEEP_CLEAR_OF_MODEL_LAMPS = 9;
const POST_H = 4.4;
const ARM = 1.35;
const POOL_R = 11;           // radius of a lamp's pool — wide enough that neighbours overlap,
                             // so a street reads as one lit ribbon from above, not a row of dots
const POOL_PEAK = 1.35;      // irradiance at the centre (× uPoolI)

// Warm sodium for the side streets, a cooler LED white for the main strip and a
// share of the rest — the mix every real city has.
const SODIUM = new THREE.Color('#ffae5e');
const LED = new THREE.Color('#e4ecff');

function hash(x, z) { const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453; return s - Math.floor(s); }

function postGeometry() {
  const parts = [];
  const pole = new THREE.CylinderGeometry(0.055, 0.075, POST_H, 8);
  pole.translate(0, POST_H / 2, 0);
  parts.push(pole);
  const base = new THREE.CylinderGeometry(0.13, 0.15, 0.32, 8);
  base.translate(0, 0.16, 0);
  parts.push(base);
  const arm = new THREE.CylinderGeometry(0.035, 0.035, ARM, 6);
  arm.rotateZ(Math.PI / 2);
  arm.translate(ARM / 2, POST_H - 0.12, 0);
  parts.push(arm);
  const head = new THREE.BoxGeometry(0.62, 0.12, 0.26);
  head.translate(ARM, POST_H - 0.12, 0);
  parts.push(head);
  return mergeGeometries(parts.map((g) => g.toNonIndexed()));
}

function bulbGeometry() {
  // The lit underside of the head, facing down.
  const g = new THREE.PlaneGeometry(0.5, 0.18);
  g.rotateX(Math.PI / 2);
  g.translate(ARM, POST_H - 0.19, 0);
  return g;
}

function coneGeometry() {
  const h = POST_H - 0.2;
  const g = new THREE.CylinderGeometry(0.2, 2.3, h, 24, 1, true);
  g.translate(ARM, h / 2, 0);
  return g;
}

const CONE_VERT = /* glsl */`
varying float vH;
varying vec3 vN;
varying vec3 vV;
varying vec3 vCol;
void main() {
  vec4 wp = modelMatrix * instanceMatrix * vec4(position, 1.0);
  vH = clamp(position.y / ${(POST_H - 0.2).toFixed(2)}, 0.0, 1.0);
  vN = normalize(mat3(modelMatrix * instanceMatrix) * normal);
  vV = normalize(cameraPosition - wp.xyz);
  #ifdef USE_INSTANCING_COLOR
    vCol = instanceColor;
  #else
    vCol = vec3(1.0);
  #endif
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const CONE_FRAG = /* glsl */`
uniform float uI;
varying float vH;
varying vec3 vN;
varying vec3 vV;
varying vec3 vCol;
void main() {
  // Brightest near the lamp, soft at the silhouette (you see through more
  // lit air looking through the middle of the cone than across its edge).
  float edge = pow(abs(dot(normalize(vN), normalize(vV))), 1.6);
  float a = pow(vH, 1.7) * edge * uI;
  gl_FragColor = vec4(vCol * a, 1.0);
}
`;

/** Place lamps along kerbs. Returns [{ x, z, dx, dz, led }]. */
function placeLamps(map, avoid) {
  const { cls, w, h, dRoad, dBlock, toWorld, unitsPerTexel: u } = map;
  const cand = [];
  for (let r = 1; r < h - 1; r++) {
    for (let x = 1; x < w - 1; x++) {
      const i = r * w + x;
      if (cls[i] !== WALK) continue;
      const dr = dRoad[i] * u;
      if (dr < 0.35 || dr > 1.0) continue;       // on the pavement, right at the kerb
      if (dBlock[i] * u < 0.7) continue;         // not against a wall or a prop
      cand.push(i);
    }
  }
  // Deterministic shuffle, so the same city always gets the same lamps.
  cand.sort((a, b) => hash(a % w, (a / w) | 0) - hash(b % w, (b / w) | 0));
  const out = [];
  const near = (x, z, list, d) => list.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < d * d);
  for (const i of cand) {
    const x = i % w; const r = (i / w) | 0;
    const [wx, wz] = toWorld(x, r);
    if (near(wx, wz, out, SPACING) || near(wx, wz, avoid, KEEP_CLEAR_OF_MODEL_LAMPS)) continue;
    // Face the road: down the gradient of the distance-to-road field.
    const gx = dRoad[i + 1] - dRoad[i - 1];
    const gz = dRoad[i + w] - dRoad[i - w];
    const gl = Math.hypot(gx, gz);
    if (gl < 1e-3) continue;
    out.push({ x: wx, z: wz, dx: -gx / gl, dz: -gz / gl, led: hash(wx, wz) > 0.7 });
  }
  return out;
}

/** Paint every lamp's pool into an RGBA8 texture laid over the city (value/4). */
function paintPools(map, lamps, modelLamps) {
  const { w, h, rect } = map;
  const acc = new Float32Array(w * h * 3);
  const sx = rect.w / w; const sz = rect.h / h;
  const splat = (cx, cz, col, peak, R) => {
    const x0 = Math.max(0, Math.floor((cx - R - rect.minX) / sx)); const x1 = Math.min(w - 1, Math.ceil((cx + R - rect.minX) / sx));
    const r0 = Math.max(0, Math.floor((cz - R - rect.minZ) / sz)); const r1 = Math.min(h - 1, Math.ceil((cz + R - rect.minZ) / sz));
    for (let r = r0; r <= r1; r++) {
      const z = rect.minZ + (r + 0.5) * sz;
      for (let x = x0; x <= x1; x++) {
        const wx = rect.minX + (x + 0.5) * sx;
        const d = Math.hypot(wx - cx, z - cz) / R;
        if (d >= 1) continue;
        // A wide soft pool with a brighter core under the head.
        const f = Math.pow(1 - d * d, 2.4) * 0.85 + Math.exp(-d * d * 22) * 0.4;
        const k = (r * w + x) * 3;
        acc[k] += col.r * f * peak; acc[k + 1] += col.g * f * peak; acc[k + 2] += col.b * f * peak;
      }
    }
  };
  for (const l of lamps) splat(l.x + l.dx * ARM, l.z + l.dz * ARM, l.led ? LED : SODIUM, POOL_PEAK, POOL_R);
  for (const m of modelLamps) splat(m.x, m.z, LED, POOL_PEAK * 0.9, POOL_R);
  const data = new Uint8Array(w * h * 4);
  for (let i = 0; i < w * h; i++) {
    data[i * 4] = Math.min(255, Math.round((acc[i * 3] / 4) * 255));
    data[i * 4 + 1] = Math.min(255, Math.round((acc[i * 3 + 1] / 4) * 255));
    data[i * 4 + 2] = Math.min(255, Math.round((acc[i * 3 + 2] / 4) * 255));
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// The model's own lamp heads (the main strip), so they light the street too.
function findModelLamps(model) {
  const out = [];
  const box = new THREE.Box3();
  const c = new THREE.Vector3();
  model.updateMatrixWorld(true);
  model.traverse((o) => {
    if (!o.isMesh) return;
    const name = (Array.isArray(o.material) ? o.material[0] : o.material)?.name || '';
    if (name !== 'WhiteGlow' && name !== 'WhiteGlow1') return;
    box.setFromObject(o); box.getCenter(c);
    out.push({ x: c.x, z: c.z, y: c.y });
  });
  return out;
}

export default function StreetLights() {
  const gl = useThree((s) => s.gl);
  const [lamps, setLamps] = useState(null);
  const built = useRef(-1);

  // Read the city once it has been batched (CityModel publishes it).
  useFrame(() => {
    if (built.current === cityBridge.version || !cityBridge.model || !cityBridge.batches || !cityBridge.bounds) return;
    built.current = cityBridge.version;
    const t0 = performance.now();
    const map = buildRoadMap(gl, cityBridge.model, cityBridge.batches, cityBridge.bounds);
    const modelLamps = findModelLamps(cityBridge.model);
    const placed = placeLamps(map, modelLamps);
    const tex = paintPools(map, placed, modelLamps);
    const prev = streetU.uPoolTex.value;
    streetU.uPoolTex.value = tex;
    streetU.uPoolRect.value.set(map.rect.minX, map.rect.minZ, map.rect.w, map.rect.h);
    if (prev && prev.image?.width > 1) prev.dispose();
    roadMapBridge.publish(map);
    setLamps(placed);
    console.log(`[streets] ${map.w}×${map.h} road map (road ${Math.round(map.stats.road * 100)}% · pavement ${Math.round(map.stats.walk * 100)}% · split at ${map.stats.threshold}) → ${placed.length} lamps + ${modelLamps.length} of the model's own, in ${Math.round(performance.now() - t0)} ms`);
  });

  const geo = useMemo(() => ({ post: postGeometry(), bulb: bulbGeometry(), cone: coneGeometry() }), []);
  const mats = useMemo(() => ({
    post: new THREE.MeshStandardMaterial({ color: '#2a2d33', roughness: 0.55, metalness: 0.6 }),
    bulb: new THREE.MeshBasicMaterial({ color: '#ffffff', toneMapped: true }),
    cone: new THREE.ShaderMaterial({
      uniforms: { uI: { value: 0 } },
      vertexShader: CONE_VERT,
      fragmentShader: CONE_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      fog: false,
    }),
  }), []);
  useEffect(() => () => {
    Object.values(geo).forEach((g) => g.dispose());
    Object.values(mats).forEach((m) => m.dispose());
  }, [geo, mats]);

  const posts = useRef(); const bulbs = useRef(); const cones = useRef();
  useEffect(() => {
    if (!lamps) return;
    const m = new THREE.Matrix4(); const q = new THREE.Quaternion(); const up = new THREE.Vector3(0, 1, 0);
    const s = new THREE.Vector3(1, 1, 1); const p = new THREE.Vector3();
    lamps.forEach((l, i) => {
      // Local +x is the arm; turn it to point at the road.
      q.setFromAxisAngle(up, Math.atan2(-l.dz, l.dx));
      m.compose(p.set(l.x, 0, l.z), q, s);
      posts.current?.setMatrixAt(i, m);
      bulbs.current?.setMatrixAt(i, m);
      cones.current?.setMatrixAt(i, m);
      const col = l.led ? LED : SODIUM;
      bulbs.current?.setColorAt(i, col);
      cones.current?.setColorAt(i, col);
    });
    for (const r of [posts, bulbs, cones]) {
      if (!r.current) continue;
      r.current.count = lamps.length;
      r.current.instanceMatrix.needsUpdate = true;
      if (r.current.instanceColor) r.current.instanceColor.needsUpdate = true;
      r.current.computeBoundingSphere();
    }
  }, [lamps]);

  // Dusk → dawn. Lamps come on a little before the sun is gone, like real ones.
  useFrame(() => {
    const on = THREE.MathUtils.smoothstep(sky.night, 0.12, 0.5);
    streetU.uPoolI.value = on;
    streetU.uWet.value = QUALITY.wetOn ? THREE.MathUtils.smoothstep(sky.night, 0.3, 0.8) * 0.85 : 0;
    streetU.uWetAll.value = QUALITY.wetOn ? weather.wet : 0;
    // Bulbs: unlit grey glass by day, HDR-bright at night so the bloom takes them.
    mats.bulb.color.setScalar(0.12 + on * 7.5);
    mats.cone.uniforms.uI.value = on * 0.1;
    if (cones.current) cones.current.visible = on > 0.01;
  });

  if (!lamps || !lamps.length) return null;
  const n = lamps.length;
  return (
    <group>
      <instancedMesh ref={posts} args={[geo.post, mats.post, n]} frustumCulled={false} />
      <instancedMesh ref={bulbs} args={[geo.bulb, mats.bulb, n]} frustumCulled={false} userData={{ noShadow: true }} />
      <instancedMesh ref={cones} args={[geo.cone, mats.cone, n]} frustumCulled={false} renderOrder={5} userData={{ noShadow: true }} />
    </group>
  );
}
